import { addHours, differenceInSeconds, isAfter, roundToNearestMinutes, subMinutes, subSeconds } from 'date-fns';
import { dropRight, map, mapValues, max, maxBy, omit, random, range, some, zip } from 'lodash';

import { DecodedSensorData, DeviceSensorConfiguration } from '@/api/device/types';
import {
    CURRENTS_DIRECTION_DATA_KEY,
    CURRENTS_SPEED_DATA_KEY,
    CURRENTS_TILT_DATA_KEY,
    ConsolidatedSensorDatum,
    GetHistoricalSensorDataParams,
    HistoricalSensorDataItem,
    PerSensor,
    RawSensorData,
    SPLRawBandStats,
    SPL_BAND_STATS_DATA_KEYS,
    SPL_BROADBAND_DATA_KEYS,
    SensorDatumValue,
    WaveSpectrumDataByTimestamp,
} from '@/api/historicalData';
import type { Detection, DetectionClassification } from '@/api/historicalData/types';
import { Units } from '@/common/convert';
import { LocationData } from '@/common/types';
import { QuantityType } from '@/components/data/Datum';
import { DateRange, useGlobalStore } from '@/store';
import {
    MOCK_DETECTION_DATA,
    MOCK_DISSOLVED_O2_DATA,
    MOCK_HYDROPHONE_DATA,
    MOCK_HYDROPHONE_HDR_READINGS_DATA,
    MOCK_SALINITY_DATA,
    MOCK_WAVE_SPECTRUM_DATA,
} from '@/utils/mockData';

export type MockDataKey =
    | 'dissolvedOxygen'
    | 'salinity'
    | 'gappyData'
    | 'gappyHdr'
    | 'multiSensor'
    | 'soundPressure'
    | 'waveSpectrum'
    | 'detections';

export const MOCK_HYDROPHONE_SENSOR_CONFIG = {
    app_name: 'borealis',
    position: 1,
    data_types: [25, 26, 27, 28, 29, 31, 14],
    firmware_sha: '0xca1a51de',
    configuration: {
        encoded: '',
        decoded: {
            enable_dsp: 1,
            enable_sbc: 1,
            dfu_confirm: 1,
            sbc_command: '/usr/local/bin/borealis_hydrotwin.sh',
            enable_pgram: 0,
            hydrotwin_hdr: 1,
            hydrotwin_ldr: 5,
            report_interval: 300,
            sensorBmLogEnable: 1,
            enable_daq_recording: 1,
            sensorsCheckIntervalS: 60,
            sensorsPollIntervalMs: 5000,
            disableUnusedPortsTimeMs: 15000,
        } as unknown as DecodedSensorData,
    },
    sensor_type_id: 21,
    configuration_crc: '0xf551127b',
    bristlemouth_node_id: '0xb3ae73485cf40ff4',
    sensor_type: {
        id: 21,
        name: 'bm_borealis_1',
        display_name: 'Bristlemouth Borealis Hydrophone',
    },
};

export const MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE = {
    id: 123456,
    configuration_checksum: '0x12345678',
    timestamp: Date.now(),
    configuration: {
        sensor_configurations: [
            {
                app_name: 'bridge',
                position: 0,
                data_types: [],
                firmware_sha: '0xe137cb3b',
                configuration: {
                    decoded: {
                        sampleDurationMs: 840000,
                        sampleIntervalMs: 1800000,
                        samplesPerReport: 2,
                        subsampleEnabled: 0,
                        softReadingPeriodMs: 500,
                        subsampleDurationMs: 60000,
                        subsampleIntervalMs: 300000,
                        ticksSamplingEnabled: 0,
                        transmitAggregations: 1,
                        alignmentInterval5Min: 1,
                        sensorsCheckIntervalS: 60,
                        sensorsPollIntervalMs: 10000,
                        currentReadingPeriodMs: 60000,
                        rbrCodaReadingPeriodMs: 500,
                        disableUnusedPortsTimeMs: 0,
                        turbidityReadingPeriodMs: 1000,
                        bridgePowerControllerEnabled: 1,
                    },
                },
                sensor_type_id: 12,
                configuration_crc: '0x235e82c9',
                bristlemouth_node_id: '0xd158263d60f8e3a1',
                sensor_type: {
                    id: 12,
                    name: 'bm_bridge_1',
                    display_name: 'Spotter',
                },
            },
            MOCK_HYDROPHONE_SENSOR_CONFIG,
        ],
    },
};

const hdrGappedTimestamps = new Set<number>();

/**
 * Creates gaps in data with processing_source='hdr' by dropping out `gapLength` data points in between each `hdrLength` points.
 *
 * THIS DOES NOT CHECK MOCK DATA SWITCHES: ONLY CALL IF YOU ALREADY KNOW!
 */
const gapifyHdr = <D extends { processing_source: string } & Timestamped>(
    data: D[],
    { gapLength, hdrLength } = { gapLength: 25, hdrLength: 75 }
): D[] => {
    let gapping = false;
    let n = 0;

    const gappyData: D[] = [];
    for (const datum of data) {
        const roundedTs = roundToNearestMinutes(
            new Date('timestamp' in datum ? datum.timestamp : datum.location.timestamp)
        ).getTime();
        if (hdrGappedTimestamps.has(roundedTs) && !gapping) {
            // skip this one, don't bother counting and hope it all comes out in the wash
            continue;
        }
        if (datum.processing_source === 'hdr') {
            n++;
            if (gapping) {
                hdrGappedTimestamps.add(roundedTs);
                if (n >= gapLength) {
                    gapping = false;
                    n = 0;
                }
            } else {
                gappyData.push(datum);
                if (n >= hdrLength) {
                    gapping = true;
                    n = 0;
                }
            }
        } else {
            gappyData.push(datum);
        }
    }
    return gappyData;
};

type Timestamped = { timestamp: string } | { location: LocationData };

/**
 * Creates gaps in data by dropping out data points. Tries to make gaps cross all data for a given timestamp.
 *
 * @param gapPattern [retainLength, skipLength, retainLength, skipLength, ...]
 *
 * THIS DOES NOT CHECK MOCK DATA SWITCHES: ONLY CALL IF YOU ALREADY KNOW!
 */
export const gapifyData = <D extends Timestamped>(data: D[], { gapPattern } = { gapPattern: [10, 1, 10, 2] }): D[] => {
    const gappedTimestamps = new Set<number>();

    let gapping = false;
    let i = 0;
    let n = 0;

    const gappyData = [];
    for (const datum of data) {
        const roundedTs = roundToNearestMinutes(
            new Date('timestamp' in datum ? datum.timestamp : datum.location.timestamp)
        ).getTime();
        if (gappedTimestamps.has(roundedTs) && !gapping) {
            // skip this one, don't bother counting and hope it all comes out in the wash
            continue;
        }
        n++;
        if (!gapping) {
            gappyData.push(datum);
        } else {
            gappedTimestamps.add(roundedTs);
        }
        if (n >= gapPattern[i]) {
            gapping = !gapping;
            n = 0;
            i = (i + 1) % gapPattern.length;
        }
    }
    return gappyData as D[];
};

/**
 * Degrades consolidated data, specific to the sensor type, only if mockDataSwitches.gappyData is enabled.
 */
export const maybeDegradeConsolidatedData = (
    sensorData: PerSensor<ConsolidatedSensorDatum[]>
): PerSensor<ConsolidatedSensorDatum[]> => {
    const { gappyData } = useGlobalStore.getState().mockDataSwitches;
    if (!gappyData) {
        return sensorData;
    }

    const MOCK_SPL_BAND_STATS_KEY = SPL_BAND_STATS_DATA_KEYS[0];
    const MOCK_SPL_BROADBAND_KEY = SPL_BROADBAND_DATA_KEYS[0];

    return mapValues(sensorData, (data) =>
        data.map((datum) => {
            if (CURRENTS_SPEED_DATA_KEY in datum.data && CURRENTS_TILT_DATA_KEY in datum.data) {
                if (Math.random() < 0.1) {
                    return {
                        ...datum,
                        data: {
                            ...omit(datum.data, CURRENTS_SPEED_DATA_KEY, CURRENTS_DIRECTION_DATA_KEY),
                            // not clearing out tilt to reflect reality that it's sometimes there in this case
                            [CURRENTS_TILT_DATA_KEY]: {
                                ...datum.data[CURRENTS_TILT_DATA_KEY],
                                value: Math.PI / 2 - Math.random() * 0.3,
                            },
                        },
                    };
                }
            }
            if (MOCK_SPL_BAND_STATS_KEY in datum.data) {
                if (Math.random() < 0.1) {
                    // degrade 10% of hydrophone data: half by clearing out all band stats ("too quiet"), half by dropping a couple bands, which
                    // happens sometimes but we don't know why
                    if (Math.random() < 0.5) {
                        return {
                            ...datum,
                            data: {
                                ...omit(datum.data, MOCK_SPL_BAND_STATS_KEY),
                                [MOCK_SPL_BROADBAND_KEY]: {
                                    ...datum.data[MOCK_SPL_BROADBAND_KEY],
                                    value: 1 + Math.random(),
                                },
                            },
                        };
                    } else {
                        const bandStats = datum.data[
                            MOCK_SPL_BAND_STATS_KEY
                        ] as unknown as SensorDatumValue<SPLRawBandStats>;
                        return {
                            ...datum,
                            data: {
                                ...datum.data,
                                [MOCK_SPL_BAND_STATS_KEY]: {
                                    ...bandStats,
                                    value: mapValues(bandStats.value, (stats) => dropRight(stats, 3)),
                                },
                            },
                        } as unknown as ConsolidatedSensorDatum;
                    }
                }
            }
            return datum;
        })
    );
};

/**
 * Creates gaps in HDR data in the input response's `data` field, only if mockDataSwitches.gappyHdr is enabled.
 *
 * Also applies mockDataSwitches.gappyData if set.
 *
 * @see gapifyHdr
 * @see gapifyData
 */
export const maybeMockHdrResponse = async <
    D extends { processing_source: string } & Timestamped,
    R extends { data: D[] },
>(
    response: R | Promise<R>
): Promise<R> => {
    const { gappyHdr, gappyData } = useGlobalStore.getState().mockDataSwitches;
    if (!gappyHdr && !gappyData) {
        return response;
    }

    if ('then' in response) {
        response = await response;
    }

    let data = response.data;
    if (gappyData) {
        data = gapifyData(data);
    }
    if (gappyHdr) {
        data = gapifyHdr(data);
    }
    return { ...response, data } as R;
};

/**
 * Creates gaps in HDR data in the input response's `data[dataFieldName]` field, only if mockDataSwitches.gappyHdr is enabled.
 *
 * Also applies mockDataSwitches.gappyData if set.
 *
 * @see gapifyHdr
 * @see gapifyData
 */
export const maybeMockNestedHdrResponse = async <
    D extends { processing_source: string } & Timestamped,
    K extends string,
    R extends { data: { [k in K]: D[] } },
>(
    response: R | Promise<R>,
    dataFieldName: K
): Promise<R> => {
    const { gappyHdr, gappyData } = useGlobalStore.getState().mockDataSwitches;
    if (!gappyHdr && !gappyData) {
        return response;
    }

    if ('then' in response) {
        response = await response;
    }

    const { data, ...moreResponse } = response;
    let nestedData = data[dataFieldName];
    if (gappyData) {
        nestedData = gapifyData(nestedData);
    }
    if (gappyHdr) {
        nestedData = gapifyHdr(nestedData);
    }

    return { ...moreResponse, data: { ...data, [dataFieldName]: nestedData } } as R;
};

/**
 * Multiplies the input data across a few more sensor positions, with a time shift to distinguish the series. Does not include
 * the input data in the output.
 *
 * THIS DOES NOT CHECK MOCK DATA SWITCHES: ONLY CALL IF YOU ALREADY KNOW!
 */
export const multiplySensorPositions = <D extends { sensorPosition: number; timestamp: string }>(data: D[]) =>
    data.flatMap(({ sensorPosition, timestamp, ...rest }) =>
        range(1, 4).map((n) => ({
            ...rest,
            sensorPosition: sensorPosition + n,
            timestamp: addHours(new Date(timestamp), n).toISOString(),
        }))
    );

export type MockDataParams = {
    sensorPosition: number;
    startDate: Date;
    endDate: Date;
    includeSoundPressureReadings?: boolean;
};

export const addMockSensorConfig = (configs: DeviceSensorConfiguration[]) => {
    const mockDataSwitches = useGlobalStore.getState().mockDataSwitches;

    const mockConfigs = {
        soundPressure: MOCK_HYDROPHONE_SENSOR_CONFIG,
    };

    Object.entries(mockConfigs).forEach(([key, sensorConfigs]) => {
        if (mockDataSwitches[key as MockDataKey]) {
            configs.push(sensorConfigs);
        }
    });
};

export const addMockSensorConfigs = (configs: unknown[]) => {
    const mockDataSwitches = useGlobalStore.getState().mockDataSwitches;

    const mockConfigs = {
        soundPressure: MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE,
    };

    Object.entries(mockConfigs).forEach(([key, sensorConfigs]) => {
        if (mockDataSwitches[key as MockDataKey]) {
            configs.push(sensorConfigs);
        }
    });
};

export const addMockData = (data: HistoricalSensorDataItem<unknown>[], params: GetHistoricalSensorDataParams) => {
    const mockDataSwitches = useGlobalStore.getState().mockDataSwitches;

    const mockDataGenerators = {
        soundPressure: mockSoundPressureData,
        dissolvedOxygen: mockDissolvedOxygenData,
        salinity: mockSalinityData,
    };

    if (!some(Object.entries(mockDataGenerators), ([key]: [MockDataKey]) => mockDataSwitches[key])) return;

    let maxSensorPosition =
        maxBy<HistoricalSensorDataItem<unknown>>(data, (d) => d.sensorPosition)?.sensorPosition ?? 0;

    Object.entries(mockDataGenerators).forEach(([key, generator]) => {
        if (mockDataSwitches[key as MockDataKey]) {
            data.push(
                ...generator({
                    sensorPosition: maxSensorPosition + 1,
                    startDate: new Date(params.startDate),
                    endDate: new Date(params.endDate),
                    includeSoundPressureReadings: params.includeSoundPressureReadings,
                })
            );
            maxSensorPosition = maxSensorPosition + 1;
        }
    });
};

export const generateMockWaveSpectrumResponse = (dateRange: DateRange | null): WaveSpectrumDataByTimestamp => {
    if (!dateRange) return {};

    const { startDate, endDate } = dateRange;
    return Object.fromEntries(
        range(startDate.getTime(), endDate.getTime(), 1000 * 3600 * 30).map((ts) => [
            new Date(ts).toISOString(),
            MOCK_WAVE_SPECTRUM_DATA,
        ])
    );
};

const generateMockSoundPressureData = ({ sensorPosition, startDate, endDate }: MockDataParams) => {
    const intervalMinutes = 30;
    const data: HistoricalSensorDataItem<SPLRawBandStats | number>[] = [];

    for (let ts = startDate.getTime(), i = 0; ts <= endDate.getTime(); ts += intervalMinutes * 60 * 1000, i++) {
        const timestamp = new Date(ts).toISOString();
        const bandNumber = [];
        const frequency = [];
        const min = [];
        const lower = [];
        const mean = [];
        const median = [];
        const upper = [];
        const max = [];

        for (const band of range(28)) {
            const base = 90 + 5 * Math.sin(ts / 5e7) + 10 * Math.sin(band / 5);
            const pressures = Array.from({ length: 7 }, () => base + (Math.random() - 0.5) * 10).sort((a, b) => a - b);

            bandNumber.push(band + 16);
            frequency.push(
                [
                    40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500,
                    3150, 4000, 5000, 6300, 8000, 10000, 12500, 16000, 20000,
                ][band]
            );
            min.push(pressures[0]);
            lower.push(pressures[1]);
            mean.push(pressures[2]);
            median.push(pressures[3]);
            upper.push(pressures[4]);
            max.push(pressures[5]);
        }

        const commonDataFields = {
            timestamp,
            latitude: 37.7889167,
            longitude: -122.3888167,
            sensorPosition,
            sensorConfigurations: MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE.id,
        };

        const omitWhiskers = (i >= 10 && i < 20) || (i >= 40 && i < 50);
        data.push(
            {
                ...commonDataFields,
                units: 'dB re µPa²',
                value: {
                    band_number: bandNumber,
                    frequency,
                    '25th_percentile': lower,
                    mean,
                    '50th_percentile': median,
                    '75th_percentile': upper,
                    ...(omitWhiskers ? {} : { '1st_percentile': min, '99th_percentile': max }),
                },
                unit_type: 'borealis_sound_pressure_level_stats',
                data_type_name: 'bm_borealis_spl_band_stats',
            },
            {
                ...commonDataFields,
                units: 'unitless',
                value: 0,
                unit_type: 'spectral_entropy',
                data_type_name: 'bm_borealis_entropy_8bits',
            },
            {
                ...commonDataFields,
                units: 'dB re µPa²',
                value: 2.25,
                unit_type: 'interquartile_range',
                data_type_name: 'bm_borealis_max_iqr_8bits',
            },
            {
                ...commonDataFields,
                units: 'unitless',
                value: maxBy(zip(bandNumber, max), (x) => x[1])?.[0] ?? -1,
                unit_type: 'index',
                data_type_name: 'bm_borealis_max_iqr_band_number_6bits',
            },
            {
                ...commonDataFields,
                units: 'dB re µPa²',
                value: mean[0],
                unit_type: 'sound_pressure_level',
                data_type_name: 'bm_borealis_spl_broadband_8bits',
            }
        );
    }
    return data;
};

const mockSoundPressureSamples = (params: MockDataParams) => {
    if (!Object.keys(MOCK_HYDROPHONE_DATA ?? {}).length) {
        return generateMockSoundPressureData(params);
    }

    const { sensorPosition, startDate, endDate } = params;
    const dateShift = differenceInSeconds(new Date(max(map(MOCK_HYDROPHONE_DATA, 'timestamp'))!), endDate);
    return MOCK_HYDROPHONE_DATA.map(({ timestamp, ...d }) => ({
        ...d,
        sensorPosition,
        sensorConfigurations: MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE.id,
        timestamp: subSeconds(new Date(timestamp), dateShift).toISOString(),
    })).filter((d) => isAfter(new Date(d.timestamp), startDate));
};

const mockSoundPressureData = (params: MockDataParams) => {
    const sampleData = mockSoundPressureSamples(params);

    if (!params.includeSoundPressureReadings) {
        return sampleData;
    }

    if (!Object.keys(MOCK_HYDROPHONE_HDR_READINGS_DATA ?? {}).length) {
        // TODO: generate?
        return sampleData;
    }

    const { sensorPosition, startDate, endDate } = params;
    const dateShift = differenceInSeconds(
        new Date(max(map(MOCK_HYDROPHONE_HDR_READINGS_DATA, 'timestamp'))!),
        subMinutes(endDate, 30)
    );
    const hdrReadings = MOCK_HYDROPHONE_HDR_READINGS_DATA.map(({ timestamp, ...d }) => ({
        ...d,
        sensorPosition,
        sensorConfigurations: MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE.id,
        timestamp: subSeconds(new Date(timestamp), dateShift).toISOString(),
    })).filter((d) => isAfter(new Date(d.timestamp), startDate));

    return [...sampleData, ...hdrReadings];
};

const generateMockDissolvedO2Data = ({ sensorPosition, startDate, endDate }: MockDataParams) => {
    const intervalMinutes = 30;
    const data: RawSensorData[] = [];

    const common = {
        configuration_checksum: MOCK_SENSOR_CONFIGS_WITH_HYDROPHONE.configuration_checksum,
        sensorPosition,
        latitude: 37.7884333,
        longitude: -122.3869167,
    };

    for (let ts = startDate.getTime(), i = 0; ts <= endDate.getTime(); ts += intervalMinutes * 60 * 1000, i++) {
        const timestamp = new Date(ts).toISOString();
        data.push(
            ...[
                {
                    ...common,
                    timestamp,
                    sensorPosition: 1,
                    units: 'mg/L' as Units,
                    value: 6 + 2 * Math.sin(i / 10) + Math.random(),
                    unit_type: 'mass_concentration' as QuantityType,
                    data_type_name: 'bm_do_mg_per_l_mean_17bits',
                },
                {
                    ...common,
                    timestamp,
                    units: 'unitless' as Units,
                    value: Math.random() < 0.1 ? 0.6 : 0.858,
                    unit_type: 'quality_ratio' as QuantityType,
                    data_type_name: 'bm_do_quality_mean_10bits',
                },
                {
                    ...common,
                    timestamp,
                    units: '%' as Units,
                    value: 50 + 25 * Math.sin(i / 10) + Math.random() * 5,
                    unit_type: 'percentage' as QuantityType,
                    data_type_name: 'bm_do_saturation_pct_mean_18bits',
                },
                {
                    ...common,
                    timestamp,
                    units: '°C' as Units,
                    value: 20 + 5 * Math.sin(i / 10) + Math.random() * 2,
                    unit_type: 'temperature' as QuantityType,
                    data_type_name: 'bm_do_temperature_mean_16bits',
                },
            ]
        );
    }

    return data;
};

export const mockDissolvedOxygenData = (params: MockDataParams) => {
    if (!Object.keys(MOCK_DISSOLVED_O2_DATA ?? {}).length) {
        return generateMockDissolvedO2Data(params);
    }

    const { sensorPosition, startDate, endDate } = params;
    const dateShift = differenceInSeconds(new Date(max(map(MOCK_DISSOLVED_O2_DATA, 'timestamp'))!), endDate);
    return MOCK_DISSOLVED_O2_DATA.map(({ timestamp, ...d }) => ({
        ...d,
        sensorPosition,
        timestamp: subSeconds(new Date(timestamp), dateShift).toISOString(),
    })).filter((d) => isAfter(new Date(d.timestamp), startDate));
};

export const mockSalinityData = (params: MockDataParams) => {
    if (!Object.keys(MOCK_SALINITY_DATA ?? {}).length) {
        // generate?
        return [];
    }

    const { sensorPosition, startDate, endDate } = params;
    const dateShift = differenceInSeconds(new Date(max(map(MOCK_SALINITY_DATA, 'timestamp'))!), endDate);
    return MOCK_SALINITY_DATA.map(({ timestamp, ...d }) => ({
        ...d,
        sensorPosition,
        timestamp: subSeconds(new Date(timestamp), dateShift).toISOString(),
    })).filter((d) => isAfter(new Date(d.timestamp), startDate));
};

const DETECTION_BASE_LATITUDE = 37.7889;
const DETECTION_BASE_LONGITUDE = -122.3888;
const DETECTION_DENSITY_THRESHOLD = 20;
const MS_PER_MINUTE = 60 * 1000;

const MIN_QUIET_GAP_MINUTES = 5;
const MAX_QUIET_GAP_MINUTES = 40;

const MOCK_DETECTION_CLASSIFICATIONS: DetectionClassification[] = ['vessels', 'dolphins', 'boats'];

export const mockDetectionData = (params: MockDataParams): Detection[] => {
    if (!Object.keys(MOCK_DETECTION_DATA ?? {}).length) {
        return generateMockDetections(params);
    }

    const { sensorPosition, startDate, endDate } = params;
    const dateShift = differenceInSeconds(new Date(max(map(MOCK_DETECTION_DATA, 'end_time'))!), endDate);
    return MOCK_DETECTION_DATA.map(({ start_time, end_time, ...d }) => ({
        ...d,
        sensor_position: sensorPosition,
        start_time: subSeconds(new Date(start_time), dateShift).toISOString(),
        end_time: subSeconds(new Date(end_time), dateShift).toISOString(),
    })).filter((d) => new Date(d.end_time).getTime() >= startDate.getTime());
};

export const generateMockDetections = ({ sensorPosition, startDate, endDate }: MockDataParams): Detection[] => {
    // will cycle through this list for as long as our time range
    const detectionRunMinutes = [
        1,
        random(5, 10),
        random(20, 30),
        1,
        random(2, 5),
        random(20, 30),
        1,
        1,
        random(20, 30),
        random(10, 20),
        random(2, 15),
    ];

    const detections: Detection[] = [];
    const endMs = endDate.getTime();

    let cursorMs = startDate.getTime();
    let regionIndex = 0;

    while (cursorMs < endMs) {
        const regionLengthMinutes = detectionRunMinutes[regionIndex % detectionRunMinutes.length];
        const detectionClass: DetectionClassification =
            MOCK_DETECTION_CLASSIFICATIONS[regionIndex % MOCK_DETECTION_CLASSIFICATIONS.length];

        for (let minute = 0; minute < regionLengthMinutes && cursorMs < endMs; minute++, cursorMs += MS_PER_MINUTE) {
            detections.push({
                classification: detectionClass,
                time_resolution: 'high',
                start_time: new Date(cursorMs).toISOString(),
                end_time: new Date(cursorMs + MS_PER_MINUTE).toISOString(),
                latitude: DETECTION_BASE_LATITUDE + (Math.random() - 0.5) * 0.01,
                longitude: DETECTION_BASE_LONGITUDE + (Math.random() - 0.5) * 0.01,
                density: random(DETECTION_DENSITY_THRESHOLD, 100),
                density_threshold: DETECTION_DENSITY_THRESHOLD,
                is_detection: true,
                bristlemouth_node_id: '0x12345',
                sensor_position: sensorPosition,
            });
        }

        const quietGapMinutes =
            MIN_QUIET_GAP_MINUTES + Math.floor(Math.random() * (MAX_QUIET_GAP_MINUTES - MIN_QUIET_GAP_MINUTES + 1));
        for (let minute = 0; minute < quietGapMinutes && cursorMs < endMs; minute++, cursorMs += MS_PER_MINUTE) {
            detections.push({
                classification: detectionClass,
                time_resolution: 'high',
                start_time: new Date(cursorMs).toISOString(),
                end_time: new Date(cursorMs + MS_PER_MINUTE).toISOString(),
                latitude: DETECTION_BASE_LATITUDE + (Math.random() - 0.5) * 0.01,
                longitude: DETECTION_BASE_LONGITUDE + (Math.random() - 0.5) * 0.01,
                density: random(0, DETECTION_DENSITY_THRESHOLD),
                density_threshold: DETECTION_DENSITY_THRESHOLD,
                is_detection: false,
                bristlemouth_node_id: '0x12345',
                sensor_position: sensorPosition,
            });
        }
        regionIndex++;
    }

    return detections;
};
